import React, { useEffect, useRef } from "react";

// Embarque un calendrier de réservation zcal dans la page.
//
// Pourquoi ce composant et pas une simple <iframe src="https://zcal.co/...">:
// le SPA de zcal détecte qu'il tourne dans une iframe et attend le handshake
// postMessage de son script d'embed officiel (iframe-resizer). Sans ce handshake,
// le calendrier reste bloqué à l'infini sur le spinner de chargement (c'est le bug
// que voyaient les visiteurs). En plus, la page publique zcal renvoie
// `X-Frame-Options: DENY` quand la requête n'a pas de Referer.
//
// On reproduit donc EXACTEMENT ce que fait le script officiel de zcal, mais de
// façon maîtrisée et rejouable à chaque montage (indispensable en SPA) :
//   1. charger une fois https://static.zcal.co/embed/v1/embed.js (fournit
//      window.iFrameResize) ;
//   2. construire l'iframe avec les paramètres d'embed attendus ;
//   3. lancer le handshake via window.iFrameResize sur cette iframe.

const ZCAL_SRC = "https://static.zcal.co/embed/v1/embed.js";
const ZCAL_VERSION = "1.0.2";

// Charge le script d'embed zcal (idempotent) et résout dès que window.iFrameResize
// est disponible.
function ensureZcalScript() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("no window"));
      return;
    }
    if (window.iFrameResize) {
      resolve();
      return;
    }
    const waitForResize = (script) => {
      let tries = 0;
      const timer = setInterval(() => {
        if (window.iFrameResize) {
          clearInterval(timer);
          resolve();
        } else if (++tries > 120) {
          clearInterval(timer);
          reject(new Error("zcal embed script timeout"));
        }
      }, 100);
      if (script) {
        script.addEventListener("error", () => {
          clearInterval(timer);
          reject(new Error("zcal embed script failed to load"));
        });
      }
    };
    let script = document.querySelector(`script[src="${ZCAL_SRC}"]`);
    if (!script) {
      script = document.createElement("script");
      script.src = ZCAL_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
    waitForResize(script);
  });
}

// Dans l'app mobile (Capacitor), l'origine de la page est `capacitor://localhost`.
// Le handshake postMessage de zcal ne fonctionne pas depuis ce schéma non standard :
// l'iframe reste blanche. On n'embarque donc PAS l'iframe dans l'app ; on ouvre le
// calendrier dans le navigateur natif (où la page zcal fonctionne parfaitement).
function isNativeApp() {
  return (
    typeof window !== "undefined" &&
    !!window.Capacitor &&
    typeof window.Capacitor.isNativePlatform === "function" &&
    window.Capacitor.isNativePlatform()
  );
}

export default function ZcalEmbed({
  url,
  className = "",
  minHeight = 640,
  title = "Calendrier de rendez-vous SF Learning",
  onLoaded,
}) {
  const containerRef = useRef(null);
  const native = isNativeApp();

  useEffect(() => {
    if (native) return undefined; // app native : pas d'iframe, on ouvre en externe
    const container = containerRef.current;
    if (!url || !container) return undefined;
    let cancelled = false;

    ensureZcalScript()
      .then(() => {
        if (cancelled || !container) return;
        const domain = window.location.href
          .replace(/^(https?:\/\/)?/, "")
          .split("/")[0]
          .split(":")[0];
        const sep = url.includes("?") ? "&" : "?";
        const src =
          `${url}${sep}embed=1&embedType=inline&embedVersion=${ZCAL_VERSION}` +
          `&embedDomain=${encodeURIComponent(domain)}`;

        const iframe = document.createElement("iframe");
        iframe.src = src;
        iframe.title = title;
        iframe.loading = "lazy";
        iframe.setAttribute(
          "style",
          `min-width:320px;width:100%;min-height:${minHeight}px;border:0;background:#fff;`
        );
        if (typeof onLoaded === "function") {
          iframe.addEventListener("load", onLoaded, { once: true });
        }
        container.innerHTML = "";
        container.appendChild(iframe);

        if (window.iFrameResize) {
          window.iFrameResize(
            {
              log: false,
              minHeight,
              scrolling: false,
              heightCalculationMethod: "taggedElement",
            },
            iframe
          );
        }
      })
      .catch(() => {
        // Repli si le script d'embed ne charge pas (bloqueur, réseau) : un lien
        // direct vers le calendrier plutôt qu'un cadre vide.
        if (cancelled || !container) return;
        container.innerHTML = "";
        const link = document.createElement("a");
        link.href = url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.className = "btn-primary";
        link.textContent = "Ouvrir le calendrier de réservation";
        container.appendChild(link);
      });

    return () => {
      cancelled = true;
      if (container) container.innerHTML = "";
    };
  }, [url, minHeight, title, onLoaded, native]);

  if (!url) return null;

  // App native : bouton qui ouvre le calendrier dans le navigateur intégré.
  //
  // L'ouverture elle-même est prise en charge par useNativeExternalLinks (un seul
  // point de branchement pour tous les liens du site). Ce composant ne doit donc
  // PAS l'ouvrir aussi de son côté : cela declenchait deux ouvertures, la seconde
  // basculant vers le navigateur du telephone apres fermeture de la premiere.
  if (native) {
    return (
      <div className={`p-6 text-center ${className}`}>
        <p className="text-sm text-gray-600 mb-4">
          Choisis ton créneau ci-dessous : le calendrier s'ouvre dans l'app, et tu
          le refermes quand tu as terminé.
        </p>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => { if (typeof onLoaded === "function") onLoaded(); }}
          className="btn-primary w-full justify-center"
          data-testid="zcal-open-native"
        >
          Choisir mon créneau
        </a>
      </div>
    );
  }

  return <div ref={containerRef} className={className} style={{ minHeight }} />;
}
